import { readFileSync } from 'node:fs';
import { resolve } from 'node:path';
import { homedir } from 'node:os';

export interface StorageConfig {
  dir: string;
  maxSessions: number;
  maxAgeDays: number;
}

export interface ProxyConfig {
  captureOn: string[];
}

export interface UiConfig {
  port: number;
  host: string;
}

export interface ScreencastConfig {
  enabled: boolean;
  quality: number;
  maxFps: number;
}

export interface ChromeConfig {
  debugPort: number;
}

export interface Config {
  storage: StorageConfig;
  proxy: ProxyConfig;
  ui: UiConfig;
  screencast: ScreencastConfig;
  chrome: ChromeConfig;
}

const DEFAULT_CAPTURABLE_TOOLS = [
  'navigate_page',
  'click',
  'fill',
  'fill_form',
  'press_key',
  'hover',
  'drag',
  'upload_file',
  'handle_dialog',
  'new_page',
];

export function getDefaults(): Config {
  return {
    storage: {
      dir: resolve(homedir(), '.browserforge/sessions'),
      maxSessions: 100,
      maxAgeDays: 30,
    },
    proxy: {
      captureOn: [...DEFAULT_CAPTURABLE_TOOLS],
    },
    ui: {
      port: 7600,
      host: '127.0.0.1',
    },
    screencast: {
      enabled: true,
      quality: 60,
      maxFps: 15,
    },
    chrome: {
      debugPort: 9222,
    },
  };
}

/** Shallow-merge each config section from a partial file config. */
function mergeFileConfig(defaults: Config, file: FileConfig): Config {
  return {
    storage: { ...defaults.storage, ...file.storage },
    proxy: {
      ...defaults.proxy,
      ...(file.proxy?.captureOn
        ? { captureOn: file.proxy.captureOn }
        : {}),
    },
    ui: { ...defaults.ui, ...file.ui },
    screencast: { ...defaults.screencast, ...file.screencast },
    chrome: { ...defaults.chrome, ...file.chrome },
  };
}

interface FileConfig {
  storage?: Partial<StorageConfig>;
  proxy?: { captureOn?: string[] };
  ui?: Partial<UiConfig>;
  screencast?: Partial<ScreencastConfig>;
  chrome?: Partial<ChromeConfig>;
}

/** Load config from ~/.browserforge/config.json if it exists. */
export function loadFileConfig(
  path?: string,
): Partial<Record<string, unknown>> {
  const configPath = path ?? resolve(homedir(), '.browserforge/config.json');
  try {
    const raw = readFileSync(configPath, 'utf-8');
    return JSON.parse(raw) as Record<string, unknown>;
  } catch {
    return {};
  }
}

export interface CliOverrides {
  port?: number;
  host?: string;
  storageDir?: string;
  chromePort?: number;
  target?: string;
}

/** Build a Config by merging defaults < file config < CLI overrides. */
export function loadConfig(
  fileConfigPath?: string,
  cli?: CliOverrides,
): Config {
  const defaults = getDefaults();
  const fileConfig = loadFileConfig(fileConfigPath) as FileConfig;
  const merged = mergeFileConfig(defaults, fileConfig);

  // Apply CLI overrides
  if (cli?.port !== undefined) merged.ui.port = cli.port;
  if (cli?.host !== undefined) merged.ui.host = cli.host;
  if (cli?.storageDir !== undefined)
    merged.storage.dir = resolve(cli.storageDir);
  if (cli?.chromePort !== undefined) merged.chrome.debugPort = cli.chromePort;

  return merged;
}
